import { getCollection, withLatency } from '@/services/mock/db'
import { httpClient } from '@/services/http/client'
import { USING_API } from '@/services/config'
import type {
  Business,
  DocRecord,
  EntityType,
  Grievance,
  Meeting,
  Opportunity,
  Organization,
  Stakeholder,
} from '@/types'

export interface SearchResult {
  type: EntityType
  id: string
  title: string
  subtitle: string
  to: string
  meta?: string
}

/** Universal search across every indexed collection. */
export const searchApi = {
  global: (q: string): Promise<SearchResult[]> => {
    if (USING_API) {
      const needle = q.trim()
      if (!needle) return Promise.resolve([])
      return httpClient.get<SearchResult[]>('/search', { params: { q: needle } }).then((res) => res.data)
    }
    return withLatency(() => {
      const needle = q.trim().toLowerCase()
      if (!needle) return []
      const results: SearchResult[] = []
      const push = (r: SearchResult) => results.push(r)

      ;(getCollection('stakeholders') as Stakeholder[]).forEach((s) => {
        const hay = `${s.firstName} ${s.lastName} ${s.phone} ${s.occupation} ${s.constituency}`.toLowerCase()
        if (hay.includes(needle))
          push({ type: 'stakeholder', id: s.id, title: `${s.firstName} ${s.lastName}`, subtitle: `${s.occupation} · ${s.constituency}`, to: `/contacts/${s.id}`, meta: s.sentiment })
      })
      ;(getCollection('businesses') as Business[]).forEach((b) => {
        if (`${b.name} ${b.industry}`.toLowerCase().includes(needle))
          push({ type: 'business', id: b.id, title: b.name, subtitle: `${b.industry} · ${b.address.district}`, to: `/businesses/${b.id}` })
      })
      ;(getCollection('organizations') as Organization[]).forEach((o) => {
        if (o.name.toLowerCase().includes(needle))
          push({ type: 'organization', id: o.id, title: o.name, subtitle: `${o.type} · ${o.memberCount} members`, to: `/directory` })
      })
      ;(getCollection('opportunities') as Opportunity[]).forEach((o) => {
        if (`${o.title} ${o.sector}`.toLowerCase().includes(needle))
          push({ type: 'opportunity', id: o.id, title: o.title, subtitle: `${o.sector} · ${o.stage}`, to: `/opportunities/${o.id}` })
      })
      ;(getCollection('grievances') as Grievance[]).forEach((g) => {
        if (`${g.reference} ${g.title} ${g.citizenName}`.toLowerCase().includes(needle))
          push({ type: 'grievance', id: g.id, title: g.title, subtitle: `${g.reference} · ${g.status}`, to: `/issues/${g.id}` })
      })
      ;(getCollection('meetings') as Meeting[]).forEach((m) => {
        if (m.title.toLowerCase().includes(needle))
          push({ type: 'meeting', id: m.id, title: m.title, subtitle: `${m.type} · ${m.location}`, to: `/meetings/${m.id}` })
      })
      ;(getCollection('documents') as DocRecord[]).forEach((d) => {
        if (`${d.name} ${d.ocrText ?? ''}`.toLowerCase().includes(needle))
          push({ type: 'document', id: d.id, title: d.name, subtitle: d.category, to: `/documents` })
      })

      return results.slice(0, 40)
    })
  },
}
